import React, {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Results from "../Results";
import { ThemeMode, ThemeProvider, useTheme } from "../theme";
import { detectSimulatedMode, resolveApiBase } from "../config";

type RunnerStatus = "idle" | "running" | "done";

interface RunCompletePayload {
  targetUrl: string;
  runId?: string;
}

interface RunnerSectionProps {
  simulatedMode: boolean;
  serverStatus: "waking" | "ready" | "error";
  serverMessage: string;
  onRetryServer: () => void;
  apiBase: string;
  onComplete: (payload: RunCompletePayload) => void;
}

interface ClientNavbarProps {
  mode: ThemeMode;
  onToggleTheme: () => void;
  onSignOut: () => void;
}

interface DevAccessGateProps {
  accessCode: string;
  onAuthorized: () => void;
}

const DEV_ACCESS_STORAGE_KEY = "launchSafely:client:devAccess";
const DEFAULT_ACCESS_CODE = "launchsafely-dev";
const configuredAccessCode =
  (process.env.REACT_APP_CLIENT_DEV_CODE || DEFAULT_ACCESS_CODE).trim();
const requiresAccessCode = configuredAccessCode.length > 0;

export default function ClientPortalApp() {
  return (
    <ThemeProvider>
      <ClientPortalContent />
    </ThemeProvider>
  );
}

function ClientPortalContent() {
  const { theme, mode, toggleMode } = useTheme();
  const simulatedMode = detectSimulatedMode();
  const apiBase = useMemo(() => resolveApiBase(), []);
  const [wakeSequence, setWakeSequence] = useState(0);
  const [serverStatus, setServerStatus] = useState<"waking" | "ready" | "error">(
    simulatedMode ? "ready" : "waking"
  );
  const [serverMessage, setServerMessage] = useState<string>(
    simulatedMode ? "Demo mode active. Backend not required." : "Waking backend..."
  );
  const [latestRun, setLatestRun] = useState<RunCompletePayload | null>(null);

  const [authorized, setAuthorized] = useState<boolean>(() => {
    if (!requiresAccessCode) return true;
    if (typeof window === "undefined") return false;
    return window.sessionStorage.getItem(DEV_ACCESS_STORAGE_KEY) === "true";
  });

  useEffect(() => {
    if (typeof document === "undefined") return;
    const previousBodyMargin = document.body.style.margin;
    const previousBodyBackground = document.body.style.background;
    const previousHtmlBackground = document.documentElement.style.background;

    document.body.style.margin = "0";
    document.body.style.background = theme.palette.background;
    document.documentElement.style.background = theme.palette.background;

    return () => {
      document.body.style.margin = previousBodyMargin;
      document.body.style.background = previousBodyBackground;
      document.documentElement.style.background = previousHtmlBackground;
    };
  }, [theme.palette.background]);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.title = "LaunchSafely • Client Area";
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (simulatedMode) {
      setServerStatus("ready");
      setServerMessage("Demo mode active. Backend not required.");
      return () => {
        cancelled = true;
      };
    }

    const maxAttempts = 3;

    const attempt = (count: number) => {
      if (cancelled) return;
      setServerStatus("waking");
      setServerMessage(
        count === 1 ? "Waking backend..." : `Waking backend (attempt ${count})...`
      );

      const healthUrl = apiBase ? `${apiBase}/health` : "/health";
      fetch(healthUrl, { cache: "no-store" })
        .then((res) => {
          if (!res.ok) throw new Error("bad status");
          if (cancelled) return;
          setServerStatus("ready");
          setServerMessage("Runner ready.");
        })
        .catch(() => {
          if (cancelled) return;
          if (count < maxAttempts) {
            setTimeout(() => attempt(count + 1), 2000);
          } else {
            setServerStatus("error");
            setServerMessage("Backend unreachable. Retry when ready.");
          }
        });
    };

    attempt(1);

    return () => {
      cancelled = true;
    };
  }, [wakeSequence, apiBase, simulatedMode]);

  const retryWake = useCallback(() => {
    setWakeSequence((seq) => seq + 1);
  }, []);

  const handleAuthorized = useCallback(() => {
    setAuthorized(true);
    if (typeof window !== "undefined") {
      window.sessionStorage.setItem(DEV_ACCESS_STORAGE_KEY, "true");
    }
  }, []);

  const handleSignOut = useCallback(() => {
    setAuthorized(false);
    setLatestRun(null);
    if (typeof window !== "undefined") {
      window.sessionStorage.removeItem(DEV_ACCESS_STORAGE_KEY);
    }
  }, []);

  if (!authorized) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: theme.palette.background,
          color: theme.palette.textPrimary,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem 1rem",
        }}
      >
        <DevAccessGate
          accessCode={configuredAccessCode}
          onAuthorized={handleAuthorized}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: theme.palette.background,
        color: theme.palette.textPrimary,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <ClientNavbar mode={mode} onToggleTheme={toggleMode} onSignOut={handleSignOut} />
      <main
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: "2rem",
          padding: "2rem clamp(1rem, 4vw, 3rem)",
        }}
      >
        <RunnerSection
          simulatedMode={simulatedMode}
          serverStatus={serverStatus}
          serverMessage={serverMessage}
          onRetryServer={retryWake}
          apiBase={apiBase}
          onComplete={(payload) => {
            setLatestRun(payload);
          }}
        />
        <section
          style={{
            background: theme.palette.card,
            borderRadius: "20px",
            border: `1px solid ${theme.palette.borderSoft}`,
            boxShadow: theme.palette.shadow,
            padding: "1.5rem",
          }}
        >
          <h2
            style={{
              margin: "0 0 1rem",
              fontSize: "1.4rem",
              fontWeight: 700,
              color: theme.palette.textPrimary,
            }}
          >
            Latest Run
          </h2>
          {latestRun ? (
            <Results
              show
              demoTargetUrl={latestRun.targetUrl}
              runId={latestRun.runId}
              onHireRequest={undefined}
            />
          ) : (
            <div
              style={{
                padding: "1.5rem",
                borderRadius: "14px",
                background: theme.palette.cardAlt,
                border: `1px dashed ${theme.palette.borderSoft}`,
                color: theme.palette.textMuted,
                textAlign: "center",
                fontSize: "1rem",
              }}
            >
              Launch a run to see the live report here.
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function RunnerSection({
  simulatedMode,
  serverStatus,
  serverMessage,
  onRetryServer,
  apiBase,
  onComplete,
}: RunnerSectionProps) {
  const { theme } = useTheme();
  const palette = theme.palette;
  const [siteUrl, setSiteUrl] = useState("");
  const [status, setStatus] = useState<RunnerStatus>("idle");
  const [progress, setProgress] = useState("");
  const [error, setError] = useState<React.ReactNode | null>(null);
  const simulatedTimers = useRef<number[]>([]);
  const serverReady = serverStatus === "ready" || simulatedMode;

  const toDisplayTarget = useCallback((value: string) => {
    const trimmed = (value || "").trim();
    if (!trimmed) return "";
    return trimmed
      .replace(/^https?:\/\//i, "")
      .replace(/^www\./i, "")
      .split("/")[0];
  }, []);

  const clearSimulatedTimers = useCallback(() => {
    if (typeof window !== "undefined") {
      simulatedTimers.current.forEach((id) => window.clearTimeout(id));
    }
    simulatedTimers.current = [];
  }, []);

  useEffect(() => {
    return () => {
      clearSimulatedTimers();
    };
  }, [clearSimulatedTimers]);

  const runSimulatedSequence = useCallback(
    (rawInput: string) => {
      const displayTarget = toDisplayTarget(rawInput);
      setStatus("running");
      setError(null);
      setProgress("Validating target URL...");

      if (typeof window === "undefined") {
        setStatus("done");
        setProgress("");
        onComplete({ targetUrl: displayTarget || "LaunchSafely Demo Site" });
        if (displayTarget) {
          setSiteUrl(displayTarget);
        }
        return;
      }

      const simulatedSteps = [
        { delay: 900, message: "Launching simulated Playwright runner..." },
        {
          delay: 1500,
          message: "Capturing screenshots and diagnostic traces...",
        },
        {
          delay: 1500,
          message: "Analyzing checks and synthesizing insights...",
        },
        { delay: 2000, message: "Finalizing report..." },
        { delay: 1000, message: "" },
      ];

      let elapsed = 0;
      simulatedSteps.forEach(({ delay, message }) => {
        elapsed += delay;
        const timeoutId = window.setTimeout(() => {
          setProgress(message);
        }, elapsed);
        simulatedTimers.current.push(timeoutId);
      });

      const finalizeId = window.setTimeout(() => {
        setStatus("done");
        setProgress("");
        onComplete({ targetUrl: displayTarget || "LaunchSafely Demo Site" });
        if (displayTarget) {
          setSiteUrl(displayTarget);
        }
        simulatedTimers.current = [];
      }, elapsed + 600);
      simulatedTimers.current.push(finalizeId);
    },
    [onComplete, toDisplayTarget]
  );

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    clearSimulatedTimers();

    if (!serverReady) {
      setError(
        "Backend is still waking up. Please wait a moment and try again."
      );
      return;
    }

    const rawInput = siteUrl.trim();
    if (!rawInput) {
      setError("Please enter a site URL to test.");
      return;
    }

    if (simulatedMode) {
      runSimulatedSequence(rawInput);
      return;
    }

    setStatus("running");
    setProgress("Starting backend run...");

    try {
      const startUrl = apiBase ? `${apiBase}/api/run/start` : `/api/run/start`;
      const resp = await fetch(startUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: rawInput, simulate: simulatedMode }),
      });

      if (!resp.ok) {
        if (resp.status === 400) {
          const data = await resp.json().catch(() => ({}));
          const message =
            data?.error === "invalid_url"
              ? "Enter a valid URL like https://example.com"
              : "Unable to start run. Please try again.";
          setError(message);
          setStatus("idle");
          setProgress("");
          return;
        }
        if (resp.status === 403) {
          const data = await resp.json().catch(() => ({}));
          const detail =
            typeof data?.message === "string"
              ? data.message
              : "Run rejected by backend policy.";
          setError(detail);
          setStatus("idle");
          setProgress("");
          return;
        }
        if (resp.status === 429) {
          const data = await resp.json().catch(() => ({}));
          if (data?.error === "rate_limited") {
            const retryMs =
              typeof data.retryAfterMs === "number" ? data.retryAfterMs : null;
            const retryMinutes = retryMs ? Math.ceil(retryMs / 60000) : null;
            setError(
              retryMinutes
                ? `Limit reached. Please wait about ${retryMinutes} minute${
                    retryMinutes > 1 ? "s" : ""
                  } and try again.`
                : "Limit reached. Please try again shortly."
            );
          } else {
            setError("Runner queue is full. Please retry in a moment.");
          }
          setStatus("idle");
          setProgress("");
          return;
        }
        throw new Error("failed to start run");
      }

      const data = await resp.json();
      const queuePosition =
        typeof data?.queuePosition === "number" ? data.queuePosition : null;
      const queueSummary =
        queuePosition && queuePosition > 1
          ? `Queued (position ${queuePosition})...`
          : "Run starting...";

      const displayTarget =
        data?.displayHost ||
        toDisplayTarget(data?.normalizedUrl || rawInput) ||
        rawInput;

      setError(null);
      onComplete({ targetUrl: displayTarget, runId: data.runId });
      if (displayTarget) {
        setSiteUrl(displayTarget);
      }
      setStatus("running");
      setProgress(queueSummary);
      setTimeout(() => {
        setStatus("done");
        setProgress("");
      }, 1500);
    } catch (err) {
      console.warn("Falling back to demo run", err);
      setProgress("Backend unavailable; showing demo report...");
      setTimeout(() => {
        setStatus("done");
        const displayTarget = toDisplayTarget(rawInput);
        setError(null);
        onComplete({ targetUrl: displayTarget || "LaunchSafely Demo Site" });
      }, 1200);
    }
  };

  return (
    <section
      style={{
        background: palette.card,
        borderRadius: "20px",
        padding: "1.8rem",
        border: `1px solid ${palette.borderSoft}`,
        boxShadow: palette.shadow,
      }}
    >
      <header
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "0.35rem",
          marginBottom: "1.5rem",
        }}
      >
        <span
          style={{
            fontSize: "0.85rem",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            color: palette.textMuted,
          }}
        >
          LaunchPad Runner
        </span>
        <h1
          style={{
            margin: 0,
            fontSize: "1.8rem",
            fontWeight: 700,
            color: palette.textPrimary,
          }}
        >
          Run a URL
        </h1>
        <p
          style={{
            margin: 0,
            color: palette.textSecondary,
            maxWidth: "42rem",
            lineHeight: 1.5,
          }}
        >
          Enter any URL to launch a full check sequence. Deep paths and custom
          flows are allowed in the client workspace.
        </p>
      </header>

      <form
        onSubmit={handleSubmit}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
        }}
      >
        <label
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.4rem",
            fontWeight: 600,
            color: palette.textPrimary,
          }}
        >
          <span>Target URL</span>
          <input
            type="url"
            placeholder="https://example.com/sign-up"
            value={siteUrl}
            onChange={(event) => setSiteUrl(event.target.value)}
            style={{
              width: "100%",
              padding: "0.9rem 1rem",
              borderRadius: "12px",
              border: `1px solid ${palette.inputBorder}`,
              background: palette.inputBackground,
              color: palette.textPrimary,
              fontSize: "1rem",
              boxShadow: "0 1px 2px rgba(15,23,42,0.05)",
            }}
          />
        </label>
        {error && (
          <div
            style={{
              color: palette.danger,
              fontSize: "0.95rem",
              lineHeight: 1.4,
            }}
          >
            {error}
          </div>
        )}
        {(progress || serverMessage) && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "0.75rem",
              padding: "0.75rem 1rem",
              borderRadius: "12px",
              background: palette.cardAlt,
              border: `1px solid ${palette.borderSoft}`,
              color:
                serverStatus === "error"
                  ? palette.danger
                  : serverStatus === "waking"
                  ? palette.warning
                  : palette.textSecondary,
              fontSize: "0.95rem",
            }}
          >
            <span>{progress || serverMessage}</span>
            {serverStatus === "error" && (
              <button
                type="button"
                onClick={onRetryServer}
                style={{
                  padding: "0.4rem 0.85rem",
                  borderRadius: "999px",
                  border: `1px solid ${palette.border}`,
                  background: palette.card,
                  color: palette.textPrimary,
                  cursor: "pointer",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                }}
              >
                Retry
              </button>
            )}
          </div>
        )}
        <button
          type="submit"
          disabled={status === "running"}
          style={{
            padding: "0.95rem 1.25rem",
            borderRadius: "12px",
            border: "none",
            fontWeight: 700,
            background: palette.accentGradient,
            color: palette.textOnAccent,
            cursor: status === "running" ? "not-allowed" : "pointer",
            boxShadow: "0 18px 32px rgba(47,145,209,0.25)",
            transition: "transform 0.2s ease",
            opacity: status === "running" ? 0.7 : 1,
          }}
        >
          {status === "running" ? "Running..." : "Run LaunchPad Checks"}
        </button>
      </form>
    </section>
  );
}

function ClientNavbar({ mode, onToggleTheme, onSignOut }: ClientNavbarProps) {
  const { theme } = useTheme();
  const palette = theme.palette;
  return (
    <header
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "1.2rem clamp(1rem, 4vw, 3rem)",
        borderBottom: `1px solid ${palette.borderSoft}`,
        background: palette.backgroundAlt,
      }}
    >
      <div style={{ display: "flex", flexDirection: "column" }}>
        <span
          style={{
            fontSize: "1.1rem",
            fontWeight: 700,
            color: palette.textPrimary,
          }}
        >
          LaunchSafely Client
        </span>
        <span style={{ color: palette.textMuted, fontSize: "0.9rem" }}>
          Internal launchpad access
        </span>
      </div>
      <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
        <button
          type="button"
          onClick={onToggleTheme}
          style={{
            padding: "0.45rem 0.95rem",
            borderRadius: "999px",
            border: `1px solid ${palette.borderSoft}`,
            background: palette.card,
            color: palette.textPrimary,
            cursor: "pointer",
            fontWeight: 600,
            fontSize: "0.9rem",
          }}
        >
          {mode === "dark" ? "Light Mode" : "Dark Mode"}
        </button>
        <button
          type="button"
          onClick={onSignOut}
          style={{
            padding: "0.45rem 0.95rem",
            borderRadius: "999px",
            border: "none",
            background: palette.accentGradient,
            color: palette.textOnAccent,
            cursor: "pointer",
            fontWeight: 600,
            fontSize: "0.9rem",
          }}
        >
          Sign out
        </button>
      </div>
    </header>
  );
}

function DevAccessGate({ accessCode, onAuthorized }: DevAccessGateProps) {
  const { theme } = useTheme();
  const palette = theme.palette;
  const requiresCode = accessCode.length > 0;
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!requiresCode) {
      onAuthorized();
      return;
    }

    const trimmed = code.trim();
    if (!trimmed) {
      setError("Enter the current access code.");
      return;
    }

    setProcessing(true);
    setTimeout(() => {
      if (trimmed === accessCode) {
        setError(null);
        onAuthorized();
      } else {
        setError("That access code does not match.");
      }
      setProcessing(false);
    }, 250);
  };

  return (
    <div
      style={{
        width: "min(420px, 100%)",
        padding: "2.5rem",
        borderRadius: "24px",
        background: palette.card,
        border: `1px solid ${palette.borderSoft}`,
        boxShadow: palette.shadow,
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "0.75rem",
          textAlign: "center",
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: "1.8rem",
            fontWeight: 700,
            color: palette.textPrimary,
          }}
        >
          LaunchSafely Access
        </h1>
        <p
          style={{
            margin: 0,
            color: palette.textSecondary,
            lineHeight: 1.5,
            fontSize: "1rem",
          }}
        >
          This workspace is limited to internal evaluations until Clerk is wired
          up. Enter the dev access code to continue.
        </p>
      </div>
      <form
        onSubmit={handleSubmit}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
        }}
      >
        {requiresCode ? (
          <label
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.5rem",
              fontWeight: 600,
              color: palette.textPrimary,
            }}
          >
            <span>Access code</span>
            <input
              type="password"
              autoFocus
              value={code}
              onChange={(event) => setCode(event.target.value)}
              placeholder="••••••••"
              style={{
                padding: "0.9rem 1rem",
                borderRadius: "12px",
                border: `1px solid ${palette.inputBorder}`,
                background: palette.inputBackground,
                color: palette.textPrimary,
                fontSize: "1rem",
                letterSpacing: "0.15em",
              }}
            />
          </label>
        ) : (
          <div
            style={{
              padding: "0.75rem 1rem",
              borderRadius: "12px",
              background: palette.cardAlt,
              border: `1px solid ${palette.borderSoft}`,
              color: palette.textSecondary,
              fontSize: "0.95rem",
            }}
          >
            Dev access is currently open. Continue below.
          </div>
        )}
        {error && (
          <div
            style={{
              color: palette.danger,
              fontSize: "0.95rem",
            }}
          >
            {error}
          </div>
        )}
        <button
          type="submit"
          style={{
            padding: "0.95rem 1.1rem",
            borderRadius: "12px",
            border: "none",
            fontWeight: 700,
            background: palette.accentGradient,
            color: palette.textOnAccent,
            cursor: "pointer",
            boxShadow: "0 18px 32px rgba(47,145,209,0.25)",
            fontSize: "1rem",
          }}
          disabled={processing}
        >
          {processing ? "Checking..." : "Enter workspace"}
        </button>
      </form>
    </div>
  );
}
